import React from 'react'
import { FormProvider } from 'react-hook-form'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'

import { isAdminDomain, isPartnerDomain } from '../../../lib/currentDomain'
import { mapChargerSettingsToChargerInformation } from '../../utils/objectMappers'
import { IChargerSettingsFormInput, useChargerSettingsValidation } from '../../hooks/useChargePointInfoValidation'
import { ChargePoint, useChargePointUpdateChargerInformationMutation } from '../../redux/api/web-api'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'

import ChargePointDetailsAdminInputs from './chargepoint-details-admin-inputs'
import ChargePointDetailsPartnerInputs from './chargepoint-details-partner-inputs'

export type ChargepointDetailsWidgetProps = {
    chargePoint: ChargePoint
}

export function ChargepointDetailsWidget({ chargePoint }: ChargepointDetailsWidgetProps): React.ReactElement {
    const text = useText()
    const chargerInfoFormValidation = useChargerSettingsValidation(chargePoint)
    const {
        handleSubmit,
        reset,
        formState: { isDirty },
    } = chargerInfoFormValidation
    const toast = useToast()
    const [updateChargerInfo, { isLoading: chargerInfoUpdating }] = useChargePointUpdateChargerInformationMutation()
    const partnerSettingsDisabled = isPartnerDomain && !!chargePoint.ocppEnabled

    const saveChargePoint = (formValues: IChargerSettingsFormInput) => {
        if (partnerSettingsDisabled) return

        updateChargerInfo({
            chargePointId: chargePoint.id,
            chargerInformation: mapChargerSettingsToChargerInformation(formValues, chargePoint),
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgChargerUpdated'))

                reset(formValues, {
                    keepDirty: false,
                    keepDirtyValues: false,
                })
            })
            .catch(() => toast.notifyError(text('msgChargePointNotUpdated')))
    }

    return (
        <div data-testid={`chargePoint-${chargePoint?.id}`}>
            {partnerSettingsDisabled && (
                <Message
                    className="w-full mb-3 justify-content-start"
                    severity="warn"
                    text={text('msgPartnerChargerSettingsDisabledByOcppProxy')}
                />
            )}

            <Card>
                <FormProvider {...chargerInfoFormValidation}>
                    {isAdminDomain ? <ChargePointDetailsAdminInputs chargePoint={chargePoint} /> : null}

                    {isPartnerDomain ? (
                        <ChargePointDetailsPartnerInputs chargePoint={chargePoint} disabled={partnerSettingsDisabled} />
                    ) : null}

                    {(isAdminDomain || isPartnerDomain) && (
                        <div className="flex justify-content-end">
                            <Button
                                label={text('btnSave')}
                                onClick={handleSubmit(saveChargePoint)}
                                disabled={partnerSettingsDisabled || chargerInfoUpdating || !isDirty}
                            />
                        </div>
                    )}
                </FormProvider>
            </Card>
        </div>
    )
}
